import { Component, ElementRef, Input, OnChanges } from '@angular/core';
import { HighchartsService } from '@app/shared/services/highcharts.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'dgx-column-chart',
    templateUrl: './column-chart.component.html',
    standalone: false
})
export class ColumnChartComponent implements OnChanges {
  @Input() public chartName;
  @Input() public tooltip;
  @Input() public subtitle;
  @Input() public series;
  @Input() public categories;
  @Input() public xAxisTitle;
  @Input() public yAxisTitle;
  @Input() public bottomText;
  @Input() chartA11yDescription: string;
  public i18n = this.translate.instant(['Core_NoResults']);
  constructor(
    private element: ElementRef,
    private Highcharts: HighchartsService,
    private tracker: TrackerService,
    private translate: TranslateService
  ) {}

  public ngOnChanges(): void {
    this.generateChart();
  }

  private trackHover(point) {
    const { tracking } = point.series.userOptions;
    if (tracking) {
      this.tracker.trackEventData(tracking);
    }
  }
  private generateChart() {
    const ctrl = this;
    const container = this.element.nativeElement.querySelector('.highcharts');
    this.Highcharts.createChart(
      container,
      {
        lang: {
          accessibility: {
            series: {
              summary: {
                column: '{series.name}',
              },
            },
          },
        },
        accessibility: {
          enabled: true,
          keyboardNavigation: {
            enabled: true,
          },
          point: {
            descriptionFormatter: (point) => {
              return `${point.category}: ${point.options.y} ${point.series.name}`;
            },
          },
        },
        title: {
          text: this.series?.length === 0 ? this.i18n.Core_NoResults : null,
          verticalAlign: 'middle',
        },
        chart: {
          style: {
            fontFamily: `'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif`,
          },
          type: 'column',
        },
        xAxis: {
          categories: this.categories,
          title: {
            enabled: !!this.xAxisTitle,
            text: this.xAxisTitle,
          },
        },
        yAxis: {
          title: {
            enabled: !!this.yAxisTitle,
            text: this.yAxisTitle,
          },
          reversedStacks: false,
          allowDecimals: false,
        },
        tooltip: {
          formatter: function () {
            ctrl.trackHover(this);
            return (
              '<b>' +
              this.x +
              '</b><br/>' +
              this.series.name +
              ': ' +
              this.y +
              '<br/>' +
              'Total: ' +
              this.point.stackTotal
            );
          },
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            pointWidth: 10,
            borderWidth: 0,
            groupPadding: 0.3,
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          itemStyle: {
            color: 'rgba(15, 31, 44, 0.61)',
            fontWeight: 'Normal',
          },
          itemDistance: 400,
          itemMarginTop: 6,
          itemMarginBottom: 6,
          align: 'left',
          x: -20,
          symbolHeight: 7,
          symbolPadding: 3,
        },
        series: this.series,
      },
      this.chartName,
      this.chartA11yDescription
    ).subscribe();
  }
}
